import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useState } from "react";
import { cn } from "@/utils/cn";

const controlBase =
  "w-full rounded-md border bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400";

interface FieldProps {
  label?: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, error, hint, required, children, className }: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-label font-medium text-foreground-800">
          {label} {required && <span className="text-primary-600">*</span>}
        </label>
      )}
      {children}
      {hint && !error && <p className="text-xs text-foreground-500">{hint}</p>}
      {error && (
        <p className="flex items-center gap-1 text-xs text-primary-700">
          <i className="ri-error-warning-line text-[13px]" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  icon?: string;
}

export function Input({ invalid, icon, className, ...rest }: InputProps) {
  return (
    <div className="relative">
      {icon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
          <i className={cn(icon, "text-base")} aria-hidden="true" />
        </span>
      )}
      <input
        className={cn(
          controlBase,
          icon && "pl-10",
          invalid ? "border-primary-400" : "border-background-300",
          className
        )}
        {...rest}
      />
    </div>
  );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function Textarea({ invalid, className, ...rest }: TextareaProps) {
  return (
    <textarea
      className={cn(controlBase, "min-h-[120px] resize-y", invalid ? "border-primary-400" : "border-background-300", className)}
      {...rest}
    />
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export function Select({ invalid, className, options, placeholder, ...rest }: SelectProps) {
  return (
    <div className="relative">
      <select
        className={cn(
          controlBase,
          "appearance-none pr-10 cursor-pointer",
          invalid ? "border-primary-400" : "border-background-300",
          className
        )}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400">
        <i className="ri-arrow-down-s-line text-lg" aria-hidden="true" />
      </span>
    </div>
  );
}

interface PasswordInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function PasswordInput({ invalid, className, ...rest }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
        <i className="ri-lock-2-line text-base" aria-hidden="true" />
      </span>
      <input
        type={visible ? "text" : "password"}
        className={cn(controlBase, "pl-10 pr-11", invalid ? "border-primary-400" : "border-background-300", className)}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
      >
        <i className={visible ? "ri-eye-off-line" : "ri-eye-line"} aria-hidden="true" />
      </button>
    </div>
  );
}